/**
 * Regional Page
 *
 * Regional settings including language, timezone, and date/number formats.
 * Uses getUserSettings query and updateUserSettings mutation from @bloomcode/graphql.
 */

import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  useToast,
} from '@bloomcode/ui';
import { MetaTags } from '@bloomcode/shared-modules';
import { useGetUserSettingsQuery, useUpdateUserSettingsMutation } from '@bloomcode/graphql';
import { Globe, Clock, Calendar, Hash, Save } from '@bloomcode/icons';

const LANGUAGES = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'es-ES', name: 'Español' },
  { code: 'fr-FR', name: 'Français' },
  { code: 'de-DE', name: 'Deutsch' },
  { code: 'pt-BR', name: 'Português (Brasil)' },
  { code: 'ja-JP', name: '日本語' },
  { code: 'zh-CN', name: '简体中文' },
  { code: 'hi-IN', name: 'हिन्दी' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'UTC', label: 'UTC' },
];

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (31.12.2024)' },
];

const NUMBER_FORMATS = [
  { value: 'en-US', label: '1,234.56 (US)' },
  { value: 'de-DE', label: '1.234,56 (EU)' },
  { value: 'fr-FR', label: '1 234,56 (FR)' },
  { value: 'en-IN', label: '1,23,456.78 (IN)' },
];

const CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
];

export default function RegionalPage() {
  const { toast } = useToast();

  const {
    data: settingsData,
    loading: settingsLoading,
    error: settingsError,
  } = useGetUserSettingsQuery();

  const [updateUserSettings, { loading: isSaving }] = useUpdateUserSettingsMutation({
    onError: (error) => {
      toast({
        title: 'Failed to save',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const settings = settingsData?.getUserSettings?.settings;

  const [formData, setFormData] = useState({
    language: 'en-US',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    numberFormat: 'en-US',
    currency: 'USD',
  });

  // Populate form when settings load
  useEffect(() => {
    if (settings) {
      // Validate that the stored numberFormat is a known locale code (e.g. 'en-US'),
      // not a display label (e.g. '1,234.56'). Fall back to 'en-US' if invalid.
      const validNumberFormatValues = NUMBER_FORMATS.map((f) => f.value);
      const resolvedNumberFormat =
        settings.numberFormat && validNumberFormatValues.includes(settings.numberFormat)
          ? settings.numberFormat
          : 'en-US';

      setFormData({
        language: settings.locale || settings.language || 'en-US',
        timezone: settings.timezone || 'America/New_York',
        dateFormat: settings.dateFormat || 'MM/DD/YYYY',
        numberFormat: resolvedNumberFormat,
        currency: settings.currencyCode || 'USD',
      });
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      const result = await updateUserSettings({
        variables: {
          input: {
            locale: formData.language,
            timezone: formData.timezone,
            currencyCode: formData.currency,
            dateFormat: formData.dateFormat,
            numberFormat: formData.numberFormat,
            language: formData.language,
          },
        },
        refetchQueries: ['GetUserSettings'],
      });

      if (result.data?.updateUserSettings?.success) {
        toast({
          title: 'Settings saved',
          description: 'Your regional settings have been updated.',
        });
      } else {
        toast({
          title: 'Failed to save',
          description: result.data?.updateUserSettings?.message || 'Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  if (settingsError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm">
          Failed to load regional settings: {settingsError.message}
        </div>
      </div>
    );
  }

  const now = new Date();

  let previewDate: string;
  try {
    previewDate = new Intl.DateTimeFormat(formData.language, {
      dateStyle: 'long',
      timeZone: formData.timezone,
    }).format(now);
  } catch {
    previewDate = now.toDateString();
  }

  let previewTime: string;
  try {
    previewTime = new Intl.DateTimeFormat(formData.language, {
      timeStyle: 'long',
      timeZone: formData.timezone,
    }).format(now);
  } catch {
    previewTime = now.toTimeString();
  }

  let previewNumber: string;
  try {
    previewNumber = new Intl.NumberFormat(formData.numberFormat, {
      style: 'currency',
      currency: formData.currency,
    }).format(1234.56);
  } catch {
    previewNumber = '1,234.56';
  }

  return (
    <>
      <Helmet>
        <title>Regional - Bloomcode Account</title>
      </Helmet>
      <MetaTags
        title="Regional Settings"
        description="Configure language, timezone, and format preferences"
      />
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Regional</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-8">
          <h1 className="text-2xl font-bold">Regional Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure your language, timezone, and format preferences
          </p>
        </div>

        <div className="space-y-6">
          {/* Language */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Language</CardTitle>
              </div>
              <CardDescription>Choose your preferred language for the interface</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-sm">
                <Label htmlFor="language">Display Language</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData({ ...formData, language: value })}
                >
                  <SelectTrigger id="language" className="mt-2">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Timezone */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Timezone</CardTitle>
              </div>
              <CardDescription>
                Set your timezone for accurate date and time display
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-sm">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={formData.timezone}
                  onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                >
                  <SelectTrigger id="timezone" className="mt-2">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Date & Time Format */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Date & Time Format</CardTitle>
              </div>
              <CardDescription>Choose how dates and times are displayed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-sm">
                <Label htmlFor="dateFormat">Date Format</Label>
                <Select
                  value={formData.dateFormat}
                  onValueChange={(value) => setFormData({ ...formData, dateFormat: value })}
                >
                  <SelectTrigger id="dateFormat" className="mt-2">
                    <SelectValue placeholder="Select date format" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FORMATS.map((fmt) => (
                      <SelectItem key={fmt.value} value={fmt.value}>
                        {fmt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Number & Currency Format */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Hash className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Number & Currency Format</CardTitle>
              </div>
              <CardDescription>Configure how numbers and currency are displayed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-sm">
                <Label htmlFor="numberFormat">Number Format</Label>
                <Select
                  value={formData.numberFormat}
                  onValueChange={(value) => setFormData({ ...formData, numberFormat: value })}
                >
                  <SelectTrigger id="numberFormat" className="mt-2">
                    <SelectValue placeholder="Select number format" />
                  </SelectTrigger>
                  <SelectContent>
                    {NUMBER_FORMATS.map((fmt) => (
                      <SelectItem key={fmt.value} value={fmt.value}>
                        {fmt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="max-w-sm">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger id="currency" className="mt-2">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((cur) => (
                      <SelectItem key={cur.value} value={cur.value}>
                        {cur.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>See how your settings will look</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <dt className="text-muted-foreground">Date</dt>
                  <dd className="font-medium">{previewDate}</dd>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <dt className="text-muted-foreground">Time</dt>
                  <dd className="font-medium">{previewTime}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-muted-foreground">Currency</dt>
                  <dd className="font-medium">{previewNumber}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
